import type { NextPageContext } from 'next';

import type { DeliveryDetails } from 'Components/pages/Delivery/DeliveryTimeline/DeliveryTimeline.types';
import {
	mapToSelectedTransportResponse,
	mapToSelectedV2TransportResponse,
} from 'Pages/api/delivery/[enquiryId]/transport.helpers';
import { resolveParams } from 'Services/services.helpers';
import type { DeliveryParams } from 'Types/delivery';
import type { PricedTransportType } from 'Types/transportOptions';
import type { User } from 'Types/user';
import isomorphicApi from 'Utilities/helpers/isomorphicApi';

import axios from '../axios';

import type { DeliveryTransportBody, DeliveryTransportBodyV2 } from './delivery.types';

export const getTransportEligibleOptions = async (params: DeliveryParams, user: User): Promise<PricedTransportType> =>
	isomorphicApi(user, {
		browser: async () => {
			const { allowAllDealers, enquiryId } = params;
			return axios.get(`/api/delivery/${enquiryId}/transport-options${resolveParams({ allowAllDealers })}`);
		},
		server: async (api) => {
			const { allowAllDealers, enquiryId } = params;
			const transportOptions = await api.getTransportEligibleOptions({ allowAllDealers, enquiryId }, user);

			return transportOptions;
		},
	});

export const getDealerSitesTransportEligibleOptions = async (
	params: DeliveryParams,
	user: User,
): Promise<PricedTransportType> =>
	isomorphicApi(user, {
		browser: async () => {
			const { enquiryId } = params;
			return axios.get(`/api/delivery/v2/${enquiryId}/transport-options`);
		},
		server: async (api) => {
			const { enquiryId } = params;
			const transportOptions = await api.getDealerSitesTransportEligibleOptions(enquiryId, user);
			return transportOptions;
		},
	});

export const saveSelectedDealerSiteTransport = async (
	params: DeliveryParams,
	ctx: NextPageContext,
): Promise<DeliveryTransportBody> =>
	isomorphicApi(ctx, {
		browser: async () => {
			const { enquiryId, payload } = params;
			return axios.post(`/api/delivery/v2/${enquiryId}/transport`, payload);
		},
		server: async (api) => {
			const { enquiryId, payload } = params;
			const selectedOption = await api.saveSelectedDealerSiteTransport(enquiryId, payload);

			return selectedOption;
		},
	});

export const getSelectedTransport = async (params: DeliveryParams, user: User): Promise<DeliveryTransportBody> =>
	isomorphicApi(user, {
		browser: async () => {
			const { enquiryId } = params;
			return axios.get(`/api/delivery/${enquiryId}/transport`);
		},
		server: async (api) => {
			const { enquiryId } = params;
			const selectedOption = await api.getSelectedTransport(enquiryId, user);

			return mapToSelectedTransportResponse(selectedOption);
		},
	});

export const getV2SelectedTransport = async (params: DeliveryParams, user: User): Promise<DeliveryTransportBodyV2> =>
	isomorphicApi(user, {
		browser: async () => {
			const { enquiryId } = params;
			return axios.get(`/api/delivery/v2/${enquiryId}/transport`);
		},
		server: async (api) => {
			const { enquiryId } = params;
			const selectedOption = await api.getV2SelectedTransport(enquiryId, user);

			return mapToSelectedV2TransportResponse(selectedOption);
		},
	});

export const saveSelectedTransport = async (
	params: DeliveryParams,
	ctx: NextPageContext,
): Promise<DeliveryTransportBody> =>
	isomorphicApi(ctx, {
		browser: async () => {
			const { enquiryId, payload } = params;
			return axios.post(`/api/delivery/${enquiryId}/transport`, payload);
		},
		server: async (api) => {
			const { enquiryId, payload } = params;
			const selectedOption = await api.saveSelectedTransport(enquiryId, payload);

			return selectedOption;
		},
	});

export const getTransportDeliveryDetails = async (params: DeliveryParams, user: User): Promise<DeliveryDetails> =>
	isomorphicApi(user, {
		browser: async () => {
			const { enquiryId } = params;
			return axios.get(`/api/transport/${enquiryId}/delivery-details`);
		},
		server: async (api) => {
			const { enquiryId } = params;
			return api.getDeliveryDetails(enquiryId, user);
		},
	});
